/** Thin fetch wrapper for the IqraPath API. Same origin; the session lives in an HttpOnly cookie. */

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  /** Seconds, from the Retry-After header (429). */
  readonly retryAfter?: number
  constructor(status: number, code: string, message: string, retryAfter?: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.retryAfter = retryAfter
  }
}

const NETWORK_MSG = 'Koneksi lagi bermasalah. Cek internet kamu, lalu coba lagi.'

export async function api<T>(method: 'GET' | 'POST' | 'PUT', path: string, body?: unknown): Promise<T> {
  let res: Response
  try {
    res = await fetch(path, {
      method,
      credentials: 'same-origin',
      // The server rejects state-changing requests that are not JSON (CSRF guard), even without a body.
      headers: method === 'GET' ? undefined : { 'content-type': 'application/json' },
      body: method === 'GET' ? undefined : JSON.stringify(body ?? {}),
    })
  } catch {
    throw new ApiError(0, 'network', NETWORK_MSG)
  }
  const data = (await res.json().catch(() => null)) as Record<string, unknown> | null
  if (!res.ok) {
    const ra = Number(res.headers.get('Retry-After'))
    throw new ApiError(
      res.status,
      typeof data?.code === 'string' ? data.code : `http_${res.status}`,
      typeof data?.message === 'string' ? data.message : 'Terjadi masalah. Coba lagi nanti.',
      Number.isFinite(ra) && ra > 0 ? ra : undefined,
    )
  }
  return data as T
}

// ---- contracts (mirror apps/api routes) ----

export type User = { id: string; email: string; name: string; role: string }
export type Reminder = { enabled: boolean; hour: number; minute: number }
export type Prefs = { jalur: 'maknai' | 'baca'; fontScale: 0 | 1 | 2; latinOn: boolean; reminder: Reminder }
export type Plan = { berjuang: string[]; jadi: string | null; menit: number | null }
export type MeResponse = {
  ok: true
  user: User
  license: { product: string; keyLast4: string; expiresAt: string | null }
  needsTerms: boolean
  termsVersion: string
  /** null until onboarding (/mulai) is saved */
  prefs: Prefs | null
}

export const redeem = (email: string, key: string) => api<{ ok: true; user: User }>('POST', '/api/auth/redeem', { email, key })
export const logout = () => api<{ ok: true }>('POST', '/api/auth/logout')
export const acceptTerms = (termsVersion: string) => api<{ ok: true; created: boolean }>('POST', '/api/me/terms', { termsVersion })
export const putPrefs = (patch: Partial<Prefs>) => api<{ ok: true; prefs: Prefs }>('PUT', '/api/me/prefs', patch)
export const putPlan = (plan: Plan) => api<{ ok: true }>('PUT', '/api/me/plan', plan)
export const putProfile = (name: string) => api<{ ok: true; name: string }>('PUT', '/api/me/profile', { name })
/** Irreversible: the server deletes the account and ends the session. */
export const deleteAccount = () => api<{ ok: true }>('POST', '/api/me/hapus-akun', { confirm: 'HAPUS' })

/** What the app should do with the current visitor. */
export type Session =
  | { state: 'anon' }
  | { state: 'blocked'; code: string; message: string }
  | { state: 'ok'; me: MeResponse }

export async function getSession(): Promise<Session> {
  try {
    return { state: 'ok', me: await api<MeResponse>('GET', '/api/auth/me') }
  } catch (e) {
    if (e instanceof ApiError) {
      if (e.status === 401) return { state: 'anon' }
      // Signed in but the license can't be used right now (expired / cannot be verified): show a clear screen, not a login loop.
      if (e.status === 403) return { state: 'blocked', code: e.code, message: e.message }
    }
    throw e
  }
}
